<template>
  <div>

    <div 
      class="estimation__info columns">

      <v-form 
        :valid.sync="valid" 
        class="column flex flex--row flex--align-center">

        <div class="px-3">
          <label class="help">Project*</label>
          <search-autocomplete
            class="mt-2"
            v-if="!$route.params.rfqId"
            v-model="projectId">
          </search-autocomplete>
          <span
            class="subtitle" 
            v-if="$route.params.rfqId && rfq">
            {{ rfq.project.project_name }}
          </span>
        </div>
        <div class="px-3">
          <label class="help">RFQ*</label>
          <template v-if="!$route.params.rfqId">
            <div
              v-if="!projectId" 
              class="my-3 has-text-grey-light">
              Please select a project
            </div>
            <v-select
              class="mt-2 rfq-selector"
              v-else
              required
              :options="rfqs"
              item-text="rfq_name"
              item-value="id"
              v-model="estimation.rfq_id"
            />
          </template>

          <span v-if="rfq" class="subtitle">
            <a :href="rfq.attachment" target="_blank">
               {{ rfq.rfq_name }}
            </a>
          </span>
        </div>
      </v-form>

      <div 
        class="column flex flex--row flex--align-center buttons">
        <button 
          class="button is-link flex--grow" 
          :class="{ 'is-loading': saving }"
          :disabled="!valid || saving"
          @click.stop="saveEstimation()">
          <span class="icon is-medium">
            <i class="fas fa-save"></i>
          </span>
          <span>              
            Save Estimation
          </span>
        </button>
        <button
          @click="adjustingParameters = true" 
          class="button">
          <span class="icon is-small">
            <i class="fas fa-cogs"></i>
          </span>
        </button>
      </div>

    </div>

    <div class="tabs mb-3 mt-2">
      <ul>
        <li 
          @click="active = 'wall'"
          :class="{ 'is-active': active == 'wall' }">
          <a>Wall</a>
        </li>
        <li 
          @click="active = 'railing'"
          :class="{ 'is-active': active == 'railing' }">
          <a>Railing</a>
        </li>
        <li 
          v-if="rfq && rfq.drawing_highlights && rfq.drawing_highlights.length > 0 && 
            rfq.drawing_highlights[0].is_approved"
          @click="active = 'highlights'"
          :class="{ 'is-active': active == 'highlights' }">
          <a>Drawing Highlights</a>
        </li>
      </ul>
    </div>

    <div class="estimation__details pb-3">
      <div 
        v-show="active=='wall'" 
        id="wall-spreadsheet" ref="spreadsheet">
      </div>
      <div
        v-show="active=='railing'" 
        id="rail-spreadsheet" ref="spreadsheet">
      </div>
      <div v-show="active=='highlights'">
        <drawing-highlights 
          v-if="rfq"
          :drawing-highlight="rfq.drawing_highlights[0]"/>
      </div>
    </div>

    <adjust-parameters-modal 
      :estimation="estimation"
      v-model="adjustingParameters"/>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VSelect from '@/components/base/VSelect'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import AdjustParametersModal from '@/components/estimations/AdjustParametersModal'
import DrawingHighlights from '@/components/estimations/DrawingHighlights'

import { estimationHelpers } from '@/mixins/helpers'

import jexcel from 'jexcel'
import 'jexcel/dist/jexcel.css'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VForm,
    VSelect,
    SearchAutocomplete,
    AdjustParametersModal,
    DrawingHighlights
  },
  mixins: [estimationHelpers],
  data() {
    return {
      active: 'wall',
      adjustingParameters: false,
      detailWidth: '500',
      wallFields: [
        'elevation', 'product_brand', 'material_id',
        'details', 'length_in', 'width_in', 'piece_qty', 'area_sqft', 'cell_q', 
        'power', 'linear_type', 'linear_in_str'
      ],
      railFields: [
        'elevation', 'material_id', 'details', 'length_in', 'width_in', 
        'piece_qty', 'area_sqft', 'solar_panels', 'power', 'linear_in_str'
      ],
      valid: true,
      projectId: null,
      rfq: null,
      estimation: {
        rfq_id: null
      },
      saving: false
    }
  },
  computed: {
    ...mapState("rfqs", ["rfqs"]),
    ...mapState("materials", ["materials"]),
    ...mapState("estimations", ["estimationParams", "paramsLoading"]),
    wallOptions({ detailWidth, wallTotCols }) {
      return {
        data: [[]],
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        updateTable: (el, cell, x) => {
          const { wallFields } = this;
          const sqftInd = wallFields.indexOf('area_sqft'),
              cellInd = wallFields.indexOf('cell_q'),
              powerInd = wallFields.indexOf('power');

          if ([sqftInd, cellInd, powerInd].includes(+x)) {
            cell.classList.add('readonly');
          }
        },
        onchange: this.wallChanged,
        columns: [
          { 
            type: 'dropdown', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Elevation', 
            source: this.elevations 
          },
          { 
            type: 'dropdown', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Product Brand', 
            source: ['Mitrex', 'Cladify']
          },
          { 
            type: 'dropdown', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Material', 
            source: this.materials,
            filter: this.wallMaterialFilter,
            autocomplete: true
          },
          { 
            type: 'text', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Details' 
          },
          { 
            type: 'text', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'L"' 
          },
          { 
            type: 'text', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'W"' 
          },
          { 
            type: 'numeric', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Quantity (Pieces)' 
          },
          {
            type: 'numeric',
            width: `${detailWidth / wallTotCols}px`,
            title: 'Total Sqft'
          },
          {
            type: 'numeric',
            width: `${detailWidth / wallTotCols}px`,
            title: 'Cell Q'
          },
          {
            type: 'numeric',
            width: `${detailWidth / wallTotCols}px`,
            title: 'Power'
          },
          { 
            type: 'dropdown', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Linear Type', 
            source: ['opening', 'pre-assemble'] 
          },
          { 
            type: 'numeric', 
            width: `${detailWidth / wallTotCols}px`, 
            title: 'Linear Inch' 
          }
        ]
      }
    },
    railOptions({ detailWidth, railTotCols }) {
      return {
        data: [[null,null,null,null,43.2,null,0]],
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        updateTable: (el, cell, x) => {
          const { railFields } = this;

          const sqftInd = railFields.indexOf('area_sqft'),
                panelInd = railFields.indexOf('solar_panels'),
                lfInd = railFields.indexOf('linear_in_str'),
                powerInd = railFields.indexOf('power');

          if ([sqftInd,panelInd,lfInd,powerInd].includes(+x)) {
            cell.classList.add('readonly');
          }
        },
        onchange: this.railChanged,
        oninsertrow: this.railInsertRow,
        columns: [
          { 
            type: 'dropdown', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'Elevation', 
            source: this.elevations 
          },
          { 
            type: 'dropdown', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'Material', 
            source: this.materials.filter(d => d.product_brand == 'Mitrex'),
            autocomplete: true
          },
          { 
            type: 'text', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'Details' 
          },
          { 
            type: 'text', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'L"' 
          },
          { 
            type: 'text', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'W"' 
          },
          { 
            type: 'numeric', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'Quantity (Pieces)' 
          },
          {
            type: 'numeric',
            width: `${detailWidth / railTotCols}px`,
            title: 'Total Sqft'
          },
          {
            type: 'numeric',
            width: `${detailWidth / railTotCols}px`,
            title: 'Solar Panels'
          },
          {
            type: 'numeric',
            width: `${detailWidth / railTotCols}px`,
            title: 'Power'
          },
          { 
            type: 'numeric', 
            width: `${detailWidth / railTotCols}px`, 
            title: 'Linear Inch' 
          }
        ]
      }
    }
  },
  watch: {
    projectId(id) {
      this.rfq = null;
      this.estimation.rfq_id = null;
      this.getRfqs({ project_id: id });
    }
  },
  created() {
    this.initialize();
  },
  mounted() {
    this.checkMaterials();
  },
  methods: {
    ...mapActions("rfqs", ["getRfqs"]),
    ...mapActions("materials", ["getMaterials"]),
    ...mapActions("estimations", [
      "addEstimation", "addDetail", "getEstimationParams"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route.params.rfq) {
        this.rfq = this.$route.params.rfq;
        this.estimation.rfq_id = this.rfq.id;
      } else if (this.$route.params.rfqId) {
        this.$http.rfq.get(this.$route.params.rfqId).then(
          resp => {
            this.rfq = resp.data;
            this.estimation.rfq_id = this.rfq.id;
          }
        )
      }

      if (Object.keys(this.estimationParams).length == 0) {
        this.getEstimationParams().then(() => {
          this.setDefaultParams();
        })
      } else {
        this.setDefaultParams();
      }
    },
    setDefaultParams() {
      const { estimationParams } = this;

      Object.keys(this.estimationParams).forEach(param => {
        this.$set(
          this.estimation, 
          param, 
          estimationParams[param]
        )
      })
    },
    initializeSpreadsheet() {
      // Get width of the spreadsheet area
      this.setWidth();

      // Initialize wall spreadsheet
      let wallSpreadsheet = jexcel(
        document.getElementById("wall-spreadsheet"), 
        this.wallOptions
      );
      Object.assign(this, { wallSpreadsheet });

      // Initialize railing spreadsheet
      let railSpreadsheet = jexcel(
        document.getElementById("rail-spreadsheet"), 
        this.railOptions
      );
      Object.assign(this, { railSpreadsheet });
    },
    saveEstimation() {
      this.saving = true;

      const {wallFields} = this;
      let wall = this.wallSpreadsheet.getData().map(d => {
        return Object.fromEntries(
          wallFields.map((_, i) => {
            let v = d[i] == "" ? null : d[i].toString() //Don't send blank, send null instead
            return [wallFields[i], v]
          })
        )
      }).filter(d => d.material_id != null && d.piece_qty != null)

      const {railFields} = this;
      let railing = this.railSpreadsheet.getData().map(d => {
        return Object.fromEntries(
          railFields.map((_, i) => {
            let v = d[i] == "" ? null : d[i].toString() //Don't send blank, send null instead
            return [railFields[i], v]
          })
        )
      }).filter(d => d.material_id != null && d.piece_qty != null)

      this.addEstimation(this.estimation).then(resp => {
        let promises = [];
        wall.forEach((detail, i) => {
          detail.estimation_id = resp.data.id;
          detail.wall_railing = "wall";
          detail.order = i;
          // delete detail.area_sqft;
          // delete detail.cell_q;
          promises.push(this.addDetail(detail));
        });
        railing.forEach((detail, i) => {
          detail.estimation_id = resp.data.id;
          detail.wall_railing = "railing";
          detail.product_brand = "Mitrex";
          detail.order = wall.length + i;
          // delete detail.area_sqft;
          // delete detail.linear_in_str;
          // delete detail.solar_panels;
          promises.push(this.addDetail(detail));
        })
        Promise.all(promises).then(() => {
          this.saving = false;
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Estimation successfully saved'
          })
          this.next(resp.data.id);
        }).catch(() => {
          this.next(resp.data.id);
          this.saving = false;
        })
      }).catch(() => {
        this.saving = false;
      })
    },
    next(id) {
      this.$router.push(
        { 
          name: 'EstimationViewer', 
          params: { estimationId: id } 
        }
      );
    }
  }
}
</script>

<style lang="scss" scoped>
.estimation {
  &__info {
    width: 100%;
    margin: 0em;
  }
  &__details {
    width: 100%;
    min-height: 100vh;
    overflow-x: auto;
    overflow-y: visible;
    padding: 0em 1em;
  }
}

.rfq-selector {
  min-width: 200px;
}

.flex {
  display: flex;
  &--row {
    flex-direction: row;
  }
  &--align-center {
    align-items: center;
  }
  &--grow {
    flex-grow: 1;
  }
}
</style>
